/**
 * Add this behavior to TokenInput view to allow for visual search ability
 * token = physical entry in search view
 * availableFields = list of things to search by that pops up when user types something in search
 * filters = actual search filters applied when searching
 */
define('../app/fukit/behavior/visual.search.behavior',['require','exports','module','underscore','backbone','marionette','../collection/base.collection'],function(require, exports, module) {
  //prettier-ignore
  var _                  = require('underscore'),
      Backbone           = require('backbone'),
      Mn                 = require('marionette'),
      BaseCollection     = require('../collection/base.collection');

  var YES_VALUE = { text: 'Yes', value: 'true' };
  var NO_VALUE = { text: 'No', value: 'false' };

  var VisualSearchBehavior = Mn.Behavior.extend({
    defaults: {
      // an array fields that are available to search by.
      // use autocompleteCollectionClass to specify what collection type will be fetched for the autocomple values
      // if autocompleCollection is not specified, class of the searchResultCollection will be inferred
      // autocompleteAttribute is used to bind text in the autocomplete dropdown to attribute in autocomplete collection.
      // useDistinctAutocompleteValues option is to remove duplicates in the autocomplete dropdown.
      // Example (user can search by name (where token will be Name) agent type (where token will be Type
      // and type values will be unique ) and active (where token will be Active and values will
      // Yes or No - default for booleans):
      // [{
      //   name: 'name',
      //   displayName: 'Name'
      // },
      // {
      //   name: 'agentType.description',
      //   displayName: 'Type',
      //   autocompleteCollectionClass: AgentTypeModel.Collection,
      //   autocompleteAttribute: 'description',
      //   useDistinctAutocompleteValues: true
      // },
      // {
      //   name: 'active',
      //   displayName: 'Active',
      //   boolean: true
      // }
      // ]
      availableFields: [],

      // and array of pre-existing filters (other than the ones on collection itself)
      // Example:
      // [{
      //  field: 'active',
      //  value: true,
      //  hidden: true
      // }]
      filters: null,

      // collection of results
      searchResultCollection: new BaseCollection(),

      // when there are no filters, collection will be emptied,
      // only applies when filters are removed or modified, initial
      // collection will not be affected if user doesn't interact with visual search
      emptyCollectionWhenNoFilters: false,

      // define custom function that executes when filters are updated and
      // can be used to perform custom logic to fetch the collection instead of
      // using the default logic.
      // Example:
      //  function(searchResultCollection, filters) {
      //    searchResultCollection.fetch();
      //    console.log('testing')
      //  }
      onUpdateFilters: false,

      // When true, allows for escaping search parameters that may contain special characters
      // Search terms escaping is defined by the specialCharRegEx regular expression
      escapeSpecialChars: false,

      // Escape quotation marks ("), commas (,), asterisks (*), and backslashes (\) by default
      // Or pass in a custom RegEx
      specialCharRegEx: /([",*\\])/g,
    },

    initialize: function() {
      this.initializeAvailableFields();
      this.initializeSearchResultCollectionAndFilters();

      // when view sets up token collection for the first time
      this.listenToOnce(this.view, 'change:collection', function() {
        if (this.view.collection) {
          this.setSearchViewTokensFromFilters();
        }
      });

      this.listenTo(this._filters, 'update reset', function() {
        this.setSearchViewTokensFromFilters();
        this.onUpdateFilters();
      })
        .listenTo(this.view, 'search:results:text', this.onChangeSearchText)
        .listenTo(this.view, 'search:results:value', this.onChangeSearchValue)
        .listenTo(this.view, 'token:remove token:add token:revised', this.onChangeTokens);
    },

    initializeAvailableFields: function() {
      var availableFields = this.getOption('availableFields');

      if (!(availableFields instanceof Backbone.Collection)) {
        this.availableFields = new Backbone.Collection(availableFields);
      } else {
        this.availableFields = availableFields;
      }
    },

    initializeSearchResultCollectionAndFilters: function() {
      this._searchResultCollection = this.getOption('searchResultCollection') || new BaseCollection();

      if (this._searchResultCollection instanceof BaseCollection) {
        this._filters = this._searchResultCollection.filters;
        if (this.getOption('filters')) {
          this._filters.add(this.getOption('filters'));
        }

        this._filters.each(function(filter) {
          var displayValue;
          var field = this.availableFields.findWhere({ name: filter.get('field').get('name') });

          if (!field) {
            filter.set('hidden', true);
            return;
          }
          var value = filter.get('value');
          if (this.getOption('escapeSpecialChars')) {
            displayValue = value;
            value = this.escapeSearchValue(value);
          } else {
            displayValue = value;
          }
          filter.set('field', field);
          filter.set('value', value);
          filter.set('displayValue', displayValue);
        }, this);
      } else {
        throw new Error("You must use a searchResultCollection that extends off of 'BaseCollection'.");
      }
    },

    getSearchViewTokensFromFilters: function() {
      var self = this;

      var visibleFilters = this._filters.filter(function(model) {
        return !model.get('hidden');
      });

      var escapeSearchValues = this.getOption('escapeSearchValue');

      var tokens = _.map(visibleFilters, function(filter) {
        //prettier-ignore
        var value = escapeSearchValues ? filter.get('displayValue') : filter.get('value'),
            field = filter.get('field');

        if (self.isFieldBoolean(field)) {
          value = value == YES_VALUE.value ? YES_VALUE.text : NO_VALUE.text;
        }

        return {
          text: field.get('displayName'),
          value: value,
        };
      });

      return tokens;
    },

    setSearchViewTokensFromFilters: function() {
      this.view.collection.set(this.getSearchViewTokensFromFilters());
    },

    onChangeSearchText: function(text) {
      var availableFieldsCollection = this.createFieldsCollection(text);
      this.setAutocompleteDropdownViewCollection(availableFieldsCollection, { text: 'displayName' });
    },

    onChangeSearchValue: function(value, text) {
      var field = this.getAvailableFieldByDisplayName(text);

      if (!field) {
        return;
      }

      var collection;

      // add special handling for boolean values
      if (this.isFieldBoolean(field)) {
        var lowerCaseValue = String(value)
          .toLowerCase()
          .trim();
        var values = _.chain([YES_VALUE, NO_VALUE])
          .filter(function(v) {
            return (
              String(v.text)
                .toLowerCase()
                .indexOf(lowerCaseValue) >= 0
            );
          })
          .value();

        collection = new Backbone.Collection(values);
        this.setAutocompleteDropdownViewCollection(collection || null, { text: 'text' });
      } else {
        collection = this.getAutocompleteCollection(value, field);
        var autocompleteDropdownBindings = this.getAutocompleteBindings(field);
        this.setAutocompleteDropdownViewCollection(collection || null, autocompleteDropdownBindings);
      }
    },

    getAutocompleteBindings: function(field) {
      if (field.get('useDistinctAutocompleteValues')) {
        return { text: 'content' };
      } else {
        return { text: field.get('autocompleteAttribute') || field.get('name') };
      }
    },

    onChangeTokens: function() {
      this.syncTokensWithFilters();
    },

    onUpdateFilters: function() {
      var onUpdateFiltersOption = this.getOption('onUpdateFilters');
      if (onUpdateFiltersOption && _.isFunction(onUpdateFiltersOption)) {
        return onUpdateFiltersOption.call(this._searchResultCollection, this._filters);
      } else if (this.getOption('emptyCollectionWhenNoFilters')) {
        var nonEmptyFilters = this._filters.filter(function(filter) {
          return !filter.get('hidden') && filter.get('value').length > 0;
        });
        if (nonEmptyFilters && nonEmptyFilters.length > 0) {
          this._searchResultCollection.fetch();
        } else {
          this._searchResultCollection.reset();
        }
        this.resetPageNumber();
      } else {
        this._searchResultCollection.fetch();
        this.resetPageNumber();
      }
    },

    resetPageNumber: function() {
      this._searchResultCollection.paginate.set('number', 1);
    },

    getAutocompleteCollection: function(value, field) {
      //prettier-ignore
      var autocompleteFieldName = field.get('autocompleteAttribute') || field.get('name'),
          autocompleteCollectionClass = field.get('autocompleteCollectionClass');
      if (!autocompleteCollectionClass) {
        autocompleteCollectionClass = this._searchResultCollection.constructor;
      }

      if (field.get('useDistinctAutocompleteValues')) {
        var url = autocompleteCollectionClass.prototype.url;

        autocompleteCollectionClass = autocompleteCollectionClass.extend({
          url: function() {
            return Mn._getValue(url, this) + '/distinct/' + encodeURIComponent(autocompleteFieldName);
          },
        });
      }

      if (!value) {
        this.autocompleteCollection = new autocompleteCollectionClass();
        this.autocompleteFilter = this.autocompleteCollection.filters.add(this.createFilterObject(value, field));
      }

      value = this.getOption('escapeSpecialChars') ? this.escapeSearchValue(value) : value;

      this.autocompleteFilter.set('value', '*' + value + '*');
      this.autocompleteCollection.sort.field(autocompleteFieldName);
      this.autocompleteCollection.urlParams(this.getOption('searchResultCollection')._urlParams.toJSON());
      this.autocompleteCollection.fetch();

      return this.autocompleteCollection;
    },

    escapeSearchValue: function(value) {
      var escapeRegEx = this.getOption('specialCharRegEx');

      return value.replace(escapeRegEx, '\\$&');
    },

    createFilterObject: function(value, field) {
      return {
        field: field.get('autocompleteAttribute') || field.get('name'),
        value: '*' + value + '*',
      };
    },

    getAvailableFieldByDisplayName: function(displayName) {
      return this.availableFields.findWhere({ displayName: displayName });
    },

    createFieldsCollection: function(searchText) {
      var self = this;

      var filteredFields = _.chain(this.availableFields.toArray())
        .filter(function(field) {
          return self.textMatchesFieldDisplayName(searchText, field);
        })
        .sortBy(function(field) {
          return field.get('displayName');
        })
        .value();

      return new Backbone.Collection(filteredFields);
    },

    textMatchesFieldDisplayName: function(searchText, field) {
      return (
        field
          .get('displayName')
          .toLowerCase()
          .indexOf(String(searchText).toLowerCase()) != -1
      );
    },

    setAutocompleteDropdownViewCollection: function(collection, bindings) {
      this.view.resultsView.childBindings = bindings;
      this.view.resultsView.collection = collection;
    },

    isFieldBoolean: function(field) {
      return !!field.get('boolean') || !!field.get('bool');
    },

    syncTokensWithFilters: function() {
      //prettier-ignore
      var fields  = this.availableFields,
          filters = [],
          tokens  = this.view.collection,
          displayValue;

      for (var i = 0, token, field, value; i < tokens.length; ++i) {
        token = tokens.at(i);
        field = this.getAvailableFieldByDisplayName(token.get('text'));
        if (!field) {
          continue;
        }
        value = token.get('value');
        if (this.isFieldBoolean(field)) {
          value = value == YES_VALUE.text ? YES_VALUE.value : NO_VALUE.value;
        }
        if (this.getOption('escapeSpecialChars')) {
          displayValue = value;
          value = this.escapeSearchValue(value);
        } else {
          displayValue = value;
        }

        value = value.replace(/%/g, '%25');

        filters.push({
          field: field,
          value: value,
          displayValue: displayValue,
        });
      }
      var hiddenFilters = this._filters.where({ hidden: true });
      this._filters.set(hiddenFilters.concat(filters));
    },
  });
  module.exports = VisualSearchBehavior;
});

